import { inject, observer } from 'mobx-react'

import { PageLoading, PageLoadingContainer } from '../../../components'
import { Page } from '../../retail/elements'
import {
  formatAmount,
  intervalAbbrev,
  intervalWord,
  priceSubtext,
} from '../../retail/retail-onboarding/pricebook-display'
import { useRetailCheckout } from '../../retail/retail-onboarding/useRetailCheckout'
import useCollapsedSidebar from '../useCollapsedSidebar'
import FeatureCards from './FeatureCards'
import {
  BodyCopy,
  CheckoutButton,
  CheckoutError,
  ContactArea,
  Disclaimer,
  Heading,
  Intro,
  MainRow,
  PageShell,
  PriceRow,
} from './styles'

const HEADING = {
  neverSubscribed: 'Finish setting up your retirement plan!',
  lapsed: 'Get back to retirement planning!',
}

/** Subscription upsell shown to a retail member without plan access. */
function RetailUpsell(props) {
  const { hasEverSubscribed, createRetailCheckoutSession } = props.store
  const { sidebarActive, toggleSidebar, expandSidebar } = props.main
  const { offer, offerUnavailable, termsOfServiceLink, checkoutStatus, startCheckout } =
    useRetailCheckout(createRetailCheckoutSession)

  useCollapsedSidebar({ sidebarActive, toggleSidebar, expandSidebar })

  const price = offer?.prices?.[0]

  if (offer === undefined) {
    return (
      <PageLoadingContainer>
        <PageLoading />
      </PageLoadingContainer>
    )
  }

  const termsOfService = termsOfServiceLink ? (
    <Page.Link href={termsOfServiceLink} target='_blank' rel='noopener noreferrer'>
      Terms of Service
    </Page.Link>
  ) : (
    'Terms of Service'
  )

  return (
    <PageShell>
      <MainRow>
        <Intro>
          <Heading>{hasEverSubscribed ? HEADING.lapsed : HEADING.neverSubscribed}</Heading>

          <BodyCopy>
            Professional financial and investment advice is proven to yield better results than
            making decisions on your own. Or worse, hoping to retire on a wing and a prayer.
          </BodyCopy>

          {price && (
            <>
              <PriceRow>
                <span className='currency'>$</span>
                <span className='amount'>{formatAmount(price.price / 100)}</span>
                <span className='interval'>/{intervalAbbrev[price.intervalType]}</span>
              </PriceRow>
              <Page.PlanPriceNote>{priceSubtext(offer)}*</Page.PlanPriceNote>
            </>
          )}

          <CheckoutButton
            type='button'
            disabled={!offer || checkoutStatus === 'pending'}
            onClick={startCheckout}>
            Secure checkout
          </CheckoutButton>

          {(checkoutStatus === 'failed' || offerUnavailable) && (
            <CheckoutError role='alert'>
              {offerUnavailable
                ? 'Subscription pricing is unavailable right now. Please call (800) 774-7459 or email help@guidedchoice.com.'
                : "We couldn't start checkout just now. Please try again."}
            </CheckoutError>
          )}

          {price && (
            <Disclaimer>
              *This subscription automatically renews every {intervalWord(price.intervalType)} and
              will continue to auto-renew until you cancel. Please see our {termsOfService} and{' '}
              <Page.Link
                href='/retail/payment/cancellation-policy'
                target='_blank'
                rel='noopener noreferrer'>
                Cancellation and Refund Policy
              </Page.Link>{' '}
              for details.
            </Disclaimer>
          )}
        </Intro>

        <FeatureCards />
      </MainRow>

      <ContactArea>
        <Page.ContactBlock />
      </ContactArea>
    </PageShell>
  )
}

export default inject('store', 'main')(observer(RetailUpsell))
